import type { Review } from '@noths/polaris-design-system-ooux-interfaces';

/** Photos shown on the card when there is no reply. More than this gets a link instead. */
const MAX_PHOTOS = 2;

/** Photos shown on the card when there is a reply, per the design's annotation. */
const MAX_PHOTOS_WITH_REPLY = 1;

/**
 * Lines of review text each shape gets, read off the Figma frame's own per-shape line
 * counts. The text gives up lines as the photo row and the reply row take space, so every
 * shape previews about the same amount of card.
 */
const MAX_LINES = {
  /* `photos` and `photosAndReply` are the same number today and are still two keys: the
     frame draws four shapes, and a change to one of them should not silently move the
     other. */
  photos: 3,
  photosAndReply: 3,
  plain: 7,
  reply: 4,
};

interface ReviewLayout {
  /**
   * Lines the text clamps to, or one less when "Read more" is showing, so the link takes
   * the line the text gives up and the region keeps the same number of lines either way.
   */
  maxLines: number;
  /** Photos to render, from the start of `review.photos`. */
  photoCount: number;
  /** Whether the "View all photos" link is needed. */
  showViewAllPhotos: boolean;
}

/**
 * How one review lays out, given what it has attached.
 *
 * Seven Figma variants, but only four shapes: whether there are photos, and whether the
 * partner replied. Kept as a pure function so the rules are testable on their own and
 * the component itself does no arithmetic.
 */
export const getReviewLayout = (review: Review): ReviewLayout => {
  const photoTotal = review.photos?.length ?? 0;
  const hasPhotos = photoTotal > 0;
  const hasReply = Boolean(review.reply);

  if (hasReply) {
    return {
      maxLines: hasPhotos ? MAX_LINES.photosAndReply : MAX_LINES.reply,
      /* A reply caps the photos at one, so anything beyond that is off the card. The
         photos it leaves off are still reachable, since the thumbnail and the reply row
         both lead to the panel that shows them all, but nothing on the card says they
         are there: "Read more" appears only when there is more to read, never to stand
         in for a missing photo affordance. See the docs page. */
      photoCount: Math.min(photoTotal, MAX_PHOTOS_WITH_REPLY),
      /* The link belongs to the photo row, which a reply has replaced with a single
         photo beside the text. */
      showViewAllPhotos: false,
    };
  }

  return {
    maxLines: hasPhotos ? MAX_LINES.photos : MAX_LINES.plain,
    photoCount: Math.min(photoTotal, MAX_PHOTOS),
    showViewAllPhotos: photoTotal > MAX_PHOTOS,
  };
};
